import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ensureSchemaType } from '@client/new/pages/Marketing/campaign-setup/useURLBuilderState';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  FormControls,
  muiForm,
  toFormControls,
} from '@client/new/helpers/forms';
import { useUpdate } from '@client/new/helpers/useUpdate';
import { Actions, F0, FSpread, TypedOmit } from '@client/new/helpers/types';
import {
  UpdatableVideo,
  UseVideosWriteModelParams,
  useVideosWriteModel,
} from '../models/videos.model';
import {
  NotificationsVM,
  useNotificationsModel,
} from '../../../../helpers/useNotifcations';
import { UploadMap, UploadState, Video } from '../models/library.domain';
import { pickObject } from '@client/new/helpers/general';
import { useEffect, useMemo, useState } from 'react';
import { omit } from 'lodash';
import toast from 'react-hot-toast';
import { uploadFile } from '../models/upload.hooks';
import { useUpload, VideoById } from '@client/new/common/videos/useUpload';
import { mixTrack } from '@services/mixpanel';
import {
  ExerciseSuggestion,
  useExerciseSuggestionsModel,
} from '@client/new/pages/creator/library/models/exercise-suggestions.model';
import auth from '@client/components/auth';
import {
  GenerateDescriptionViewModel,
  useGenerateDescriptionVM,
} from '@client/new/pages/creator/library/view-models/generateDescription.viewModel';

type PayloadMap = {
  selectThumbnail: number;
  restoreThumbnail: never;
  submitEdit: never;
  setEditingMode: EditVMState['editingMode'];
  exerciseTitleUpdate: {
    description: string;
    uuid: string;
  };
  applyHint: { hint: Hint; uuid: string };
  showReplaceConfirmation: never;
  replaceVideo: never;
  closeConfirmation: never;
  replaceUpload: File;
  cancelSingleUpload: never;
  clearReplaceFile: never;
};

export type VideoView = Pick<
  Video,
  | 'url'
  | 'thumbnailUrl'
  | 'duration'
  | 'width'
  | 'height'
  | 'downloadUrl'
  | 'name'
  | 'id'
> & {
  isMux: boolean;
};

export const toVideoView = (v: Video): VideoView => ({
  ...pickObject(v, [
    'url',
    'thumbnailUrl',
    'duration',
    'width',
    'height',
    'downloadUrl',
    'name',
    'id',
  ]),
  isMux: v.url.includes('mux'),
});

export type EditVMAction = Actions<PayloadMap>;
export type EditVMDispatcher = FSpread<PayloadMap>;

type EditableVideo = TypedOmit<UpdatableVideo, 'id' | 'videoSourceType'>;

const schema = ensureSchemaType<EditableVideo>()(
  z.object({
    name: z.string().nonempty('Field is required'),
    description: z.string().optional(),
    thumbnailTimestamp: z.number().optional(),
  }),
);

type EditVMState = {
  submitting: boolean;
  video: VideoView;
  editingMode: 'Details' | 'Thumbnail' | 'Replace';
  showReplaceConfirmation: boolean;
  isUploading: boolean;
  isProcessing: boolean;
  uploadError: boolean;
  uploadedId: string | null;
  replacingFile?: File;
};

export type EditVideoVM = EditVMState & {
  dispatch: EditVMDispatcher;
  form: FormControls<EditableVideo>;
  notifications: NotificationsVM;
  generateDescriptions: GenerateDescriptionViewModel;
  influencerId: number;
  tagLabel: 'Clip' | 'Video';
  inputHints: InputHints;
  uuid: string;
  uploadState: UploadState;
  exerciseSuggestions: ExerciseSuggestion[];
  canSeeExperimentalFeatures: boolean;
};

export type UseEditVideoVMParams = UseVideosWriteModelParams & {
  video: Video;
  folderId: number | null;
  onClose: F0;
  onEditSuccess: () => void;
  onReplaceSuccess: (video: VideoById) => void;
};

export type Hint = { title?: string; description?: string };
type InputHints = Record<string, Hint>;

const uploadFileId = '1';

export const useEditVideoVM = (p: UseEditVideoVMParams): EditVideoVM => {
  const writeModel = useVideosWriteModel({ influencerId: p.influencerId });
  const { pushNotification, ...notifications } = useNotificationsModel();
  const generateDescriptionVM = useGenerateDescriptionVM();

  const [inputHints, setInputHints] = useState<InputHints>({});
  const [uploadMap, setUploadMap] = useState<UploadMap>({});
  const [state, updateState] = useUpdate<EditVMState>({
    submitting: false,
    video: toVideoView(p.video),
    editingMode: 'Details',
    showReplaceConfirmation: false,
    isUploading: false,
    uploadedId: null,
    isProcessing: false,
    uploadError: false,
    replacingFile: undefined,
  });

  const canSeeExperimentalFeatures = useMemo<boolean>(
    () =>
      (import.meta.env.VITE_EXPERIMENTAL_FEATURES_USERS || '')
        .split(',')
        .includes(auth.getUser().email),
    [],
  );

  const { uploadFinished, startPolling } = useUpload({
    influencerId: p.influencerId,
    uploadedId: state.uploadedId,
    addVideo: (video: VideoById) => p.onReplaceSuccess(video),
  });

  useEffect(() => {
    if (uploadFinished && state.uploadedId) {
      updateState({
        editingMode: 'Details',
        uploadedId: null,
        replacingFile: undefined,
        video: toVideoView(p.video),
      });
    }
  }, [state.uploadedId, updateState, uploadFinished]);

  const form = muiForm(
    useForm<EditableVideo, any, EditableVideo>({
      defaultValues: {
        name: p.video.name,
        description: p.video.description,
        thumbnailTimestamp: 0,
      },
      resolver: zodResolver(schema),
    }),
  );

  const { exerciseSuggestions } = useExerciseSuggestionsModel(
    form.watch('name'),
  );

  const dispatch: EditVMDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as EditVMAction;
    switch (action.type) {
      case 'restoreThumbnail':
        form.setValue('thumbnailTimestamp', undefined);
        return pushNotification('Custom', 'Thumbnail restored!');
      case 'selectThumbnail': {
        updateState;
        form.setValue('thumbnailTimestamp', action.payload);
        toast.success('Thumbnail changed!', { duration: 1000 });
        return null;
      }
      case 'submitEdit':
        onSubmitEdit();
        return null;
      case 'setEditingMode':
        return updateState({ editingMode: action.payload });
      case 'exerciseTitleUpdate': {
        const { description, uuid } = action.payload;
        const currentDescription = form.getValues('description');
        if (currentDescription) {
          setInputHints((prev) => ({
            ...prev,
            [uuid]: {
              ...(prev[uuid] || {}),
              description,
            },
          }));
          return null;
        }
        form.setValue('description', description);
        return null;
      }
      case 'applyHint': {
        const { hint, uuid } = action.payload;

        const updatedhints = omit(inputHints, [uuid]);
        setInputHints(updatedhints);
        if (hint.title) {
          form.setValue('name', hint.title);
        }
        if (hint.description) {
          form.setValue('description', hint.description);
        }
        return null;
      }
      case 'closeConfirmation':
        return updateState({ showReplaceConfirmation: false });
      case 'replaceVideo':
        return updateState({
          showReplaceConfirmation: false,
          editingMode: 'Replace',
        });
      case 'showReplaceConfirmation':
        return updateState({ showReplaceConfirmation: true });
      case 'replaceUpload':
        updateState({ replacingFile: action.payload });
        upload(action.payload);
        return null;
      case 'cancelSingleUpload':
        clearUploadMap();
        return updateState({
          replacingFile: undefined,
          editingMode: 'Details',
        });
      case 'clearReplaceFile':
        return updateState({
          replacingFile: undefined,
          editingMode: 'Details',
        });
    }
  };

  const clearUploadMap = () => {
    if (uploadMap[uploadFileId]?.muxInstance) {
      uploadMap[uploadFileId].muxInstance.abort();
    }
    setUploadMap((prev) => {
      const next = { ...prev };
      delete next['1'];
      return next;
    });
  };

  const onSubmitEdit = form.handleSubmit(async (payload) => {
    updateState({ submitting: true });
    try {
      await writeModel.updateVideo(
        {
          ...payload,
          id: p.video.id + '',
          type: p.video.type,
          videoSourceType: p.video.videoSourceType,
        },
        { ...form.formState.defaultValues },
      );
      p.onClose();
      p.onEditSuccess();
      pushNotification('Success', 'Video updated successfully!');
    } catch (e) {
      pushNotification('Error', 'Something went wrong!');
    } finally {
      updateState({ submitting: false });
    }
  });

  const onUploadSuccess = (uploadedId: string) => {
    updateState({ isProcessing: true, uploadedId });
    startPolling(3000);
  };

  const upload = async (file: File) => {
    updateState({ isUploading: true });
    mixTrack({
      type: 'Video Upload Started',
      payload: { 'Number of Videos': 1 },
    });
    const updateFile = new File([file], p.video.name, {
      type: file.type,
    });
    uploadFile({
      video: {
        file: updateFile,
        id: uploadFileId,
      },
      influencerId: p.influencerId,
      setUploadMap,
      uploadMap,
      onSuccess: (uploadedId: string) => onUploadSuccess(uploadedId),
      folderId: p.folderId,
    })
      .then(() => {
        writeModel.deleteVideo({
          id: p.video.id,
          videoSourceType: p.video.videoSourceType,
        });
      })
      .catch((e) => {
        pushNotification('Error', 'Something went wrong!');
        return updateState({
          editingMode: 'Details',
          uploadError: true,
        });
      })
      .finally(() => {
        updateState({ isUploading: false });
      });
  };

  const tagLabel = p.video.type === 'long' ? 'Video' : 'Clip';

  return {
    ...state,
    form: toFormControls(form),
    dispatch,
    notifications,
    generateDescriptions: generateDescriptionVM,
    influencerId: p.influencerId,
    tagLabel,
    inputHints,
    uuid: uploadFileId,
    uploadState: uploadMap[uploadFileId],
    exerciseSuggestions,
    canSeeExperimentalFeatures,
  };
};
